import { STATUS } from 'utils/constants'

// REFERENCE TOKENS

export const neutral = {
  100: '#FFFFFF', // White
  200: '#F6F7F8', // Light Gray
  300: '#ECF0F4',
  400: '#DDE2E8', // Gray
  500: '#B3BCC6',
  800: '#262626',
  900: '#1A1A1A', // Charcoal
  1000: '#000000', // Black
}

export const neutralCool = {
  75: '#FAFBFC',
  200: '#F4F4F6',
  300: '#EDF1F5',
  600: '#B8C0C4',
  700: '#76878E',
  900: '#1F2D3D', // Gunmetal
}

export const neutralWarm = {
  500: '#FCF8F0',
  600: '#ECE7DC',
}

export const palette = {
  primary: {
    a: { 500: '#EB6961' }, // Salmon
    b: { 500: '#620D3E' }, // Plum
  },
  secondary: {
    a: { 500: '#214064' }, // Picotee
    b: { 100: '#EAF5FF', 500: '#00A0FF' }, // Uranian Blue
    c: { 500: '#F4CD84' }, // Cantaloupe
  },
  duo: {
    a: { 1: '#19968C', 2: '#D2E7E6' }, // Green
    b: { 1: '#AF568A', 2: '#EBDAE5' }, // Plum
  },
}

export const status = {
  [STATUS.SYSTEM.ACTIVE]: '#2D8EFF',
  [STATUS.SYSTEM.SUCCESS]: '#53CA48',
  [STATUS.SYSTEM.ERROR]: '#FF0000',
  [STATUS.SYSTEM.WARNING]: '#FF8600',
  disabled: neutralCool[300], // #EDF1F5
}

// SYSTEM TOKENS

export const text = {
  primary: neutral[800],
  main_1000: neutral[1000],
  main: neutral[800],
  neutral_300: neutral[300],
  inverse: neutral[100],
  neutralCool_900: neutralCool[900],
  neutralCool_700: neutralCool[700],
  mainCool: neutralCool[700],
  neutral_500: neutral[500], // #B3BCC6
  neutralCool_75: neutralCool[75], // #FAFBFC
  primaryHue: palette.primary.a[500],
  secondary: palette.secondary.a[500],
  secondaryC: palette.secondary.c[500],
  tertiary: palette.secondary.b[500],
  hitA: palette.duo.b[1],
  placeholder: neutral[500],
  inherit: 'inherit',
  transparent: 'transparent',
  ...status,
}

const primary = {
  main: palette.primary.a[500],
  text: text.inverse,
}

const secondary = {
  main: palette.secondary.a[500],
  text: text.inverse,
}

export const background = {
  darkSolid: {
    main: neutral[1000],
    text: text.inverse,
  },

  main: {
    main: neutral[100],
    text: text.main,
  },
  primary: {
    main: palette.primary.a[500],
    text: text.inverse,
  },
  primary_b500: {
    main: palette.primary.b[500],
    text: text.inverse,
  },
  secondary: {
    main: palette.secondary.a[500],
    text: text.inverse,
  },
  secondary_b500: {
    main: palette.secondary.b[500],
    text: text.inverse,
  },
  default: {
    main: neutral[200],
    text: text.main,
  },
  paper: {
    main: neutral[100],
    text: text.main,
  },
  neutral_300: {
    main: neutral[300],
    text: text.main,
  },
  neutral_400: {
    main: neutral[400],
    text: text.main,
  },
  neutral_500: {
    main: neutral[500],
    text: text.inverse,
  },
  neutralCool_75: {
    main: neutralCool[75], // #FAFBFC
    text: text.main,
  },
  neutralCool_200: {
    main: neutralCool[200],
    text: text.main,
  },
  neutralCool: {
    main: neutralCool[300], // #EDF1F5
    text: text.main,
  },
  neutralCool_700: {
    main: neutralCool[700],
    text: neutralCool[300], // #EDF1F5
  },
  neutralCool_900: {
    main: neutralCool[900],
    text: text.inverse,
  },
  inverse: {
    main: neutral[800],
    text: text.inverse,
  },
  neutral_900: {
    main: neutral[900],
    text: text.inverse,
  },
  neutral_1000: {
    main: neutral[1000],
    text: text.inverse,
  },
  ghostInverse: {
    main: 'rgba(0, 0, 0, .2)',
    text: neutral[100],
  },
  hitA: {
    main: '#AF568A',
    text: text.inverse,
  },
  [STATUS.SYSTEM.ACTIVE]: {
    main: status[STATUS.SYSTEM.ACTIVE],
    text: text.inverse,
  },
  [STATUS.SYSTEM.SUCCESS]: {
    main: status[STATUS.SYSTEM.SUCCESS],
    text: text.inverse,
  },
  [STATUS.SYSTEM.ERROR]: {
    main: status[STATUS.SYSTEM.ERROR],
    text: text.inverse,
  },
  [STATUS.SYSTEM.WARNING]: {
    main: status[STATUS.SYSTEM.WARNING],
    text: text.inverse,
  },
  disabled: {
    main: status.disabled,
    text: text.neutralCool_700,
  },
  transparent: {
    main: 'transparent',
    text: 'inherit',
  },
  duoA: {
    main: palette.duo.a[2],
    text: palette.duo.a[1],
  },
  duoB: {
    main: palette.duo.b[2],
    text: palette.duo.b[1],
  },
  duoError: {
    main: '#FFE5E5',
    text: status[STATUS.SYSTEM.ERROR],
  },
  duoSuccess: {
    main: 'rgba(83, 202, 72, .2)',
    text: status[STATUS.SYSTEM.SUCCESS],
  },
  tertiaryLight: {
    main: palette.secondary.b[100],
    text: palette.secondary.b[500],
    borderColor: 'transparent',
  },
  neutralWarm: {
    main: neutralWarm[500],
    text: text.main,
  },
  neutralWarmDark: {
    main: neutralWarm[600],
    text: text.main,
  },
}

export const border = {
  neutral_100: neutral[100],
  neutral_200: neutral[200],
  main: neutral[300],
  neutral_300: neutral[300],
  neutral_400: neutral[400],
  neutral_500: neutral[500], // #B3BCC6
  text: text.main,
  neutral_900: neutral[900],
  neutralCool_75: neutralCool[75], // #FAFBFC
  neutralCool_200: neutralCool[200],
  mainCool: neutralCool[300], // #EDF1F5
  neutralCool_300: neutralCool[300], // #EDF1F5
  neutralCool_700: neutralCool[700],
  primary: neutral[300],
  primaryHue: palette.primary.a[500],
  neutralCool_600: neutralCool[600],
  secondary: neutral[300],
  secondary_a500: palette.secondary.a[500],
  tertiary: palette.secondary.b[500],
  hitA: palette.duo.b[1],
  transparent: 'transparent',
  none: 'none',
  ...status,
}

const color = {
  neutral,
  neutralCool,
  primary,
  secondary,
  palette,
  status,
  background,
  text,
  border,
}

export default color
